import React from 'react';
import { Select as AntSelect, SelectProps as AntSelectProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

const { Option, OptGroup } = AntSelect;

export type SelectVariant = 'default' | 'secondary';

export interface SelectProps<ValueType = any> extends Omit<AntSelectProps<ValueType>, 'className' | 'dropdownClassName'> {
  variant?: SelectVariant;
  className?: string;
  dropdownClassName?: string;
  status?: 'error' | 'warning';
}

const useStyles = createUseStyles((theme: ThemeType) => {
  const createSelectStyles = (variant: SelectVariant = 'default') => {
    const bg = variant === 'default' ? theme.color.semantic.background.neutral.secondary : theme.color.semantic.brand.default.muted;
    const border = variant === 'default' ? theme.color.semantic.border.primary : theme.color.semantic.brand.default.subtle;

    return {
      // Select selector (main input area)
      '& .ant-select-selector': {
        height: '32px !important',
        backgroundColor: `${bg} !important`,
        borderColor: `${border} !important`,
        borderWidth: theme.border.width.sm,
        borderRadius: `${theme.radius.sm} !important`,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.regular,
        padding: `${theme.spacing[0]} ${theme.spacing[3]} !important`,
        '& .ant-select-selection-placeholder': {
          color: theme.color.semantic.text.soft,
          opacity: theme.opacity[100],
        },
        '& .ant-select-selection-item': {
          color: theme.color.semantic.text.primary,
          fontSize: theme.typography.fontSize.sm,
          fontWeight: theme.typography.fontWeight.regular,
        },
        '& .ant-select-selection-search-input': {
          fontSize: theme.typography.fontSize.sm,
          color: theme.color.semantic.text.primary,
        },
      },
      // Hover state
      '&:not(.ant-select-disabled):hover .ant-select-selector': {
        backgroundColor: `${bg} !important`,
        borderColor: `${theme.color.semantic.brand.default.soft} !important`,
      },
      // Focus state
      '&.ant-select-focused .ant-select-selector': {
        backgroundColor: `${bg} !important`,
        borderColor: `${theme.color.semantic.brand.default.moderate} !important`,
        boxShadow: 'none !important',
      },
      // Disabled state
      '&.ant-select-disabled': {
        '& .ant-select-selector': {
          backgroundColor: `${theme.color.semantic.background.neutral.subtle} !important`,
          opacity: `${theme.opacity[70]} !important`,
          color: theme.color.semantic.text.soft,
          borderColor: `${theme.color.semantic.border.primary} !important`,
          cursor: 'not-allowed',
          '& .ant-select-selection-placeholder': {
            color: theme.color.semantic.text.soft,
          },
          '& .ant-select-selection-item': {
            color: theme.color.semantic.text.soft,
          },
        },
      },
      // Arrow icon
      '& .ant-select-arrow': {
        color: theme.color.semantic.text.soft,
        fontSize: theme.typography.fontSize.sm,
        right: theme.spacing[3],
        '& .anticon': {
          color: theme.color.semantic.text.soft,
        },
      },
      '&.ant-select-disabled .ant-select-arrow': {
        color: theme.color.semantic.text.soft,
        '& .anticon': {
          color: theme.color.semantic.text.soft,
        },
      },
      // Single select - no tag background
      '& .ant-select-selection-item': {
        backgroundColor: 'transparent',
        border: 'none',
      },
      // Clear button
      '& .ant-select-clear': {
        right: theme.spacing[3],
        color: theme.color.semantic.text.soft,
        fontSize: theme.typography.fontSize.sm,
        backgroundColor: bg,
        '&:hover': {
          color: theme.color.semantic.text.primary,
        },
      },
      // Status error
      '&.select-status-error': {
        '& .ant-select-selector': {
          borderColor: `${theme.color.semantic.brand.danger.light} !important`,
        },
        '&.ant-select-focused .ant-select-selector': {
          borderColor: `${theme.color.semantic.brand.danger.light} !important`,
        },
      },
      // Status warning
      '&.select-status-warning': {
        '& .ant-select-selector': {
          borderColor: `${theme.color.semantic.accent.orange.strong} !important`,
        },
        '&.ant-select-focused .ant-select-selector': {
          borderColor: `${theme.color.semantic.accent.orange.strong} !important`,
        },
      },
      // Size variants - Small
      '&.ant-select-sm': {
        '& .ant-select-selector': {
          height: '24px !important',
          padding: `${theme.spacing[0]} ${theme.spacing[2]} !important`,
          fontSize: theme.typography.fontSize.sm,
          '& .ant-select-selection-item': {
            fontSize: theme.typography.fontSize.sm,
          },
          '& .ant-select-selection-placeholder': {
            fontSize: theme.typography.fontSize.sm,
          },
        },
      },
      // Small multiple mode - Allow height to expand
      '&.ant-select-sm.ant-select-multiple .ant-select-selector': {
        height: 'auto !important',
        minHeight: '24px !important',
      },
      // Size variants - Large
      '&.ant-select-lg': {
        '& .ant-select-selector': {
          height: '40px !important',
          padding: `${theme.spacing[0]} ${theme.spacing[4]} !important`,
          fontSize: theme.typography.fontSize.md,
          '& .ant-select-selection-item': {
            fontSize: theme.typography.fontSize.md,
          },
          '& .ant-select-selection-placeholder': {
            fontSize: theme.typography.fontSize.md,
          },
        },
      },
      // Large multiple mode - Allow height to expand
      '&.ant-select-lg.ant-select-multiple .ant-select-selector': {
        height: 'auto !important',
        minHeight: '40px !important',
      },
      // Multiple mode - Allow height to expand
      '&.ant-select-multiple .ant-select-selector': {
        height: 'auto !important',
        minHeight: '32px !important',
      },
      // Multiple mode - Tags
      '&.ant-select-multiple .ant-select-selection-item': {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
        border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
        borderRadius: `${theme.radius.sm} !important`,
        color: theme.color.semantic.text.primary,
        fontSize: theme.typography.fontSize.sm,
        padding: `0 ${theme.spacing[2]}`,
        marginTop: theme.spacing[1],
        marginBottom: theme.spacing[1],
        '& .ant-select-selection-item-remove': {
          color: theme.color.semantic.text.primary,
          fontSize: theme.typography.fontSize.sm,
          '&:hover': {
            color: theme.color.semantic.text.primary,
          },
        },
      },
      // Multiple mode - Tag close icon
      '& .ant-select-selection-item-remove': {
        color: theme.color.semantic.text.primary,
        '&:hover': {
          color: theme.color.semantic.text.primary,
        },
      },
    };
  };

  const createDropdownStyles = (variant: SelectVariant = 'default') => {
    const bg = variant === 'default' ? theme.color.semantic.background.neutral.secondary : theme.color.semantic.brand.default.muted;
    const border = variant === 'default' ? theme.color.semantic.border.primary : theme.color.semantic.brand.default.subtle;

    return {
      '&.ant-select-dropdown': {
        backgroundColor: `${bg} !important`,
        borderRadius: `${theme.radius.sm} !important`,
        boxShadow: `${theme.shadow.md} !important`,
        border: `${theme.border.width.sm} solid ${border} !important`,
        padding: `${theme.spacing[1]} !important`,
      },
      '& .ant-select-item': {
        color: `${theme.color.semantic.text.primary} !important`,
        fontSize: `${theme.typography.fontSize.sm} !important`,
        padding: `${theme.spacing[2]} ${theme.spacing[3]} !important`,
        borderRadius: `${theme.radius.sm} !important`,
      },
      '& .ant-select-item:hover': {
        backgroundColor: `${theme.color.semantic.background.neutral.primary} !important`,
      },
      '& .ant-select-item-option-selected': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
        color: `${theme.color.semantic.text.primary} !important`,
        fontWeight: `${theme.typography.fontWeight.medium} !important`,
        '& .anticon': {
          color: `${theme.color.semantic.text.primary} !important`,
        },
      },
      '& .ant-select-item-option-active': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
      },
      '& .ant-select-item-option-disabled': {
        color: `${theme.color.semantic.text.soft} !important`,
        cursor: 'not-allowed !important',
        opacity: `${theme.opacity[60]} !important`,
      },
      '& .ant-select-item-group': {
        color: `${theme.color.semantic.text.soft} !important`,
        fontSize: `${theme.typography.fontSize.xs} !important`,
        fontWeight: `${theme.typography.fontWeight.medium} !important`,
        padding: `${theme.spacing[1]} ${theme.spacing[3]} !important`,
      },
      '& .ant-select-item-empty': {
        color: `${theme.color.semantic.text.soft} !important`,
        fontSize: `${theme.typography.fontSize.sm} !important`,
        padding: `${theme.spacing[2]} ${theme.spacing[3]} !important`,
      },
      '& .ant-select-dropdown-search': {
        padding: `${theme.spacing[2]} !important`,
      },
      '& .ant-select-dropdown-search .ant-input': {
        backgroundColor: `${bg} !important`,
        borderColor: `${border} !important`,
        borderRadius: `${theme.radius.sm} !important`,
        fontSize: `${theme.typography.fontSize.sm} !important`,
      },
      '& .ant-select-dropdown-search .ant-input:focus': {
        borderColor: `${theme.color.semantic.brand.default.moderate} !important`,
      },
    };
  };

  return {
    defaultSelect: createSelectStyles('default'),
    secondarySelect: createSelectStyles('secondary'),
    defaultSelectDropdown: createDropdownStyles('default'),
    secondarySelectDropdown: createDropdownStyles('secondary'),
  };
});

// Default filter function that searches option labels
const defaultFilterOption = (input: string, option: any): boolean => {
  const label = option?.children || option?.label || '';
  return String(label).toLowerCase().includes(input.toLowerCase());
};

function SelectComponent<ValueType = any>({
  size = 'middle',
  variant = 'default',
  className = '',
  status,
  dropdownClassName,
  showSearch = true,
  filterOption = defaultFilterOption,
  ...rest
}: SelectProps<ValueType>) {
  const classes = useStyles();

  // Build className with variant and status handling
  const variantClass = variant === 'secondary' ? classes.secondarySelect : classes.defaultSelect;
  const statusClass = status ? `select-status-${status}` : '';
  const combinedClassName = `${variantClass} ${statusClass} ${className}`.trim();

  // Combine dropdown class with variant-specific styles
  const dropdownVariantClass = variant === 'secondary'
    ? classes.secondarySelectDropdown
    : classes.defaultSelectDropdown;
  const combinedDropdownClassName = `${dropdownVariantClass} ${dropdownClassName || ''}`.trim();

  return (
    <AntSelect
      size={size}
      showSearch={showSearch}
      filterOption={filterOption}
      {...(rest as any)}
      className={combinedClassName}
      dropdownClassName={combinedDropdownClassName}
    />
  );
}

export const Select = SelectComponent as typeof SelectComponent & {
  Option: typeof Option;
  OptGroup: typeof OptGroup;
};

// Attach sub-components
Select.Option = Option;
Select.OptGroup = OptGroup;

export default Select;
